import apiClient from '../../utils/apiClient';

class ReportsService {
  async getReportHTML(eventId) {
    const response = await apiClient.get(`/eventos/${encodeURIComponent(eventId)}/reporte/html/`);
    
    if (response.ok) {
      return await response.text();
    }
    
    if (response.status === 400) {
      try {
        const responseText = await response.text();
        
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message) {
            throw new Error(errorData.message);
          }
        } catch (jsonError) {
          const decodedText = responseText
            .replace(/&amp;quot;/g, '"')
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&');
          
          const jsonMatch = decodedText.match(/\{[^}]*"success"[^}]*\}/g);
          if (jsonMatch) {
            try {
              const errorData = JSON.parse(jsonMatch[0]);
              if (errorData.message) {
                throw new Error(errorData.message);
              }
            } catch (extractError) {}
          }
        }
      } catch (parseError) {}
      throw new Error('Este evento no tiene invitados registrados o no se ha validado correctamente.');
    } else if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {}
      throw new Error('No tienes permisos para ver reportes de este evento');
    } else if (response.status === 404) {
      throw new Error('El endpoint de reportes no está disponible en el backend');
    } else if (response.status === 500) {
      throw new Error('Debes validar en el calendario los invitados antes de generar el reporte.');
    }
    
    // Intentar obtener mensaje del backend para cualquier error
    try {
      const errorData = await response.json();
      if (errorData.message) {
        throw new Error(errorData.message);
      }
    } catch (parseError) {}
    
    throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
  }

  async getReportJSON(eventId) {
    const response = await apiClient.get(`/eventos/${encodeURIComponent(eventId)}/reporte/`);
    
    if (response.ok) {
      return await response.json();
    }
    
    if (response.status === 400) {
      try {
        const responseText = await response.text();
        
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message) {
            throw new Error(errorData.message);
          }
        } catch (jsonError) {
          const decodedText = responseText
            .replace(/&amp;quot;/g, '"')
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&');
          
          const jsonMatch = decodedText.match(/\{[^}]*"success"[^}]*\}/g);
          if (jsonMatch) {
            try {
              const errorData = JSON.parse(jsonMatch[0]);
              if (errorData.message) {
                throw new Error(errorData.message);
              }
            } catch (extractError) {}
          }
        }
      } catch (parseError) {}
      throw new Error('Este evento no tiene invitados registrados o no se ha validado correctamente.');
    } else if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {}
      throw new Error('No tienes permisos para ver reportes de este evento');
    } else if (response.status === 404) {
      throw new Error('El endpoint de reportes no está disponible en el backend');
    } else if (response.status === 500) {
      throw new Error('Debes validar en el calendario los invitados antes de generar el reporte.');
    }
    
    try {
      const errorData = await response.json();
      if (errorData.message) {
        throw new Error(errorData.message);
      }
    } catch (parseError) {}
    
    throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
  }

  async downloadReport(eventId, format, eventTitle = null) {
    const response = await apiClient.get(`/eventos/${encodeURIComponent(eventId)}/reporte/descargar/${format}/`);
    
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extraer filename del Content-Disposition header del backend
      const contentDisposition = response.headers.get('Content-Disposition') || 
                                response.headers.get('content-disposition');
      
      let filename;
      
      if (contentDisposition) {
        // Múltiples patrones para extraer filename
        const patterns = [
          /filename\*?=["']?([^"';\n]*)["']?/i,
          /filename=["']?([^"';\n]*)["']?/i,
          /attachment;\s*filename=["']?([^"';\n]*)["']?/i
        ];
        
        for (const pattern of patterns) {
          const match = contentDisposition.match(pattern);
          if (match && match[1]) {
            filename = decodeURIComponent(match[1].trim());
            // Filename extraído correctamente
            break;
          }
        }
      }
      
      // Fallback si no hay Content-Disposition
      if (!filename) {
        const extension = format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'html';
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        
        if (eventTitle) {
          // Limpiar título para nombre de archivo
          const cleanTitle = eventTitle
            .replace(/[^a-zA-Z0-9\s]/g, '') // Remover caracteres especiales
            .replace(/\s+/g, '_') // Espacios a guiones bajos
            .substring(0, 50); // Máximo 50 caracteres
          filename = `Reporte_${cleanTitle}_${today}.${extension}`;
        } else {
          filename = `Reporte_Evento_${today}.${extension}`;
        }
        
        // Usando filename por defecto mejorado
      }
      
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return true;
    }
    
    if (response.status === 400) {
      // Intentar obtener el mensaje específico del backend
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {
        // Si no se puede parsear, usar mensaje genérico
      }
      throw new Error('Este evento no tiene invitados registrados o no se ha validado correctamente.');
    } else if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {}
      throw new Error('No tienes permisos para descargar reportes de este evento');
    } else if (response.status === 404) {
      throw new Error('El endpoint de descarga no está disponible en el backend');
    } else if (response.status === 500) {
      throw new Error('Debes validar en el calendario los invitados antes de generar el reporte.');
    }
    
    try {
      const errorData = await response.json();
      if (errorData.message) {
        throw new Error(errorData.message);
      }
    } catch (parseError) {}
    
    try {
      const errorData = await response.json();
      if (errorData.message) {
        throw new Error(errorData.message);
      }
    } catch (parseError) {}
    
    throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
  }
  
  async downloadReportCustom(customUrl, eventTitle, format) {
    const response = await apiClient.get(customUrl);
    
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generar nombre de archivo
      const extension = format === 'xlsx' ? 'xlsx' : 'pdf';
      const today = new Date().toISOString().split('T')[0];
      const tipoReporte = customUrl.includes('solo_asistentes=false') ? 'Todos_Invitados' : 'Solo_Asistentes';
      
      let filename;
      if (eventTitle) {
        const cleanTitle = eventTitle
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 30);
        filename = `${cleanTitle}_${tipoReporte}_${today}.${extension}`;
      } else {
        filename = `Reporte_${tipoReporte}_${today}.${extension}`;
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return true;
    }
    
    if (response.status === 400) {
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {}
      throw new Error('Este evento no tiene invitados registrados o no se ha validado correctamente.');
    } else if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {}
      throw new Error('No tienes permisos para descargar reportes de este evento');
    } else if (response.status === 404) {
      throw new Error('El endpoint de descarga no está disponible en el backend');
    } else if (response.status === 500) {
      throw new Error('Debes validar en el calendario los invitados antes de generar el reporte.');
    }
    
    try {
      const errorData = await response.json();
      if (errorData.message) {
        throw new Error(errorData.message);
      }
    } catch (parseError) {}
    
    throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
  }

  processHtmlContent(htmlContent, eventTitle = null) {
    let processedContent = htmlContent;
    
    // Reemplazar títulos incorrectos si se proporciona el título correcto
    if (eventTitle) {
      processedContent = processedContent.replace(/Evento_\d+/g, eventTitle);
    }
    
    processedContent = processedContent.replace(/<h1>/g, '<h1 style="color: #262B40; font-family: Roboto;">');
    processedContent = processedContent.replace(/<h2>/g, '<h2 style="color: #262B40; font-family: Roboto;">');
    processedContent = processedContent.replace(/<table>/g, '<table style="width: 100%; border-collapse: collapse; font-family: Roboto;">');
    processedContent = processedContent.replace(/<th>/g, '<th style="background-color: #f8f9fa; padding: 8px; border: 1px solid #dee2e6;">');
    processedContent = processedContent.replace(/<td>/g, '<td style="padding: 8px; border: 1px solid #dee2e6;">');
    
    const customCSS = `
      <style>
        body { font-family: 'Roboto', sans-serif; margin: 20px; }
        .report-header { background-color: #262B40; color: white; padding: 15px; border-radius: 5px; }
        .report-content { margin-top: 20px; }
      </style>
    `;
    
    if (processedContent.includes('<head>')) {
      processedContent = processedContent.replace('</head>', customCSS + '</head>');
    } else {
      processedContent = customCSS + processedContent;
    }
    
    return processedContent;
  }

  async getProcessedReportHTML(eventId, eventTitle = null) {
    try {
      const htmlContent = await this.getReportHTML(eventId);
      return this.processHtmlContent(htmlContent, eventTitle);
    } catch (error) {
      console.error('ReportsService.getProcessedReportHTML error:', error);
      throw error; // Re-lanzar el error para que se propague
    }
  }

  async getValidatedEvents() {
    // Usar endpoint único que incluye información de permisos
    try {
      const response = await apiClient.get('/eventos/listar/');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.eventos) {
          return data.eventos;
        } else if (data.success && data.data) {
          return data.data;
        } else if (Array.isArray(data)) {
          return data;
        }
      }
    } catch (error) {
      console.warn('Error loading events:', error);
    }
    
    return [];
  }


}

export default new ReportsService();